import type { StartedFeatureName } from "@/api/splashpages";
import { useStartedFeatures } from "@/hooks/useHasEngagedWithFeature";

export const DEFAULT_NAV_BADGE_TTL_DAYS = 30;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

function isWithinTtl(newSince: Date, ttlDays: number): boolean {
  const since = newSince.getTime();
  if (Number.isNaN(since)) return false;
  return Date.now() < since + ttlDays * MS_PER_DAY;
}

// Controls visibility of a "New" nav badge. The badge shows only while both
// expiration gates are open:
//   1. Time gate: the TTL window since `newSince` (a UTC `Date`) has not
//      elapsed.
//   2. Interaction gate: the user has not yet clicked "Get Started" on the
//      feature, tracked in the `started_features` table (the same record the
//      splash pages write). Because that query is updated optimistically on
//      click, the badge disappears immediately, without a page reload.
// Returns false as soon as either gate closes. A nav item with no `featureKey`
// has no interaction gate and relies on the time gate alone.
export function useNavNewBadge(
  featureKey: StartedFeatureName | undefined,
  newSince: Date | undefined,
  ttlDays: number = DEFAULT_NAV_BADGE_TTL_DAYS,
): boolean {
  const { startedFeatures, loading } = useStartedFeatures();

  if (!newSince) return false;
  if (!isWithinTtl(newSince, ttlDays)) return false;

  // Hide until engagement state is known so an already-engaged user never sees
  // the badge flash in and then out.
  if (loading) return false;

  if (featureKey && startedFeatures.has(featureKey)) {
    return false;
  }

  return true;
}
